import { Component, Host, h, Prop, State, Listen, Element } from '@stencil/core';
import { Event, EventEmitter, HTMLStencilElement, Method } from '@stencil/core/internal';
import { WindowResizeService } from '../../services/window-resize-service';
import { ContentLineTextAlignOptions, ContentVerticalAlignments, ResponsiveBoolean, ResponsiveString } from '../../typings';
import * as focusTrap from 'focus-trap';
import './libraries/aria-utilities';
import './libraries/aria-dialog';
/**
 * @slot closeButton - Contents of the overlay close button.
 * @slot header - This is adjacent to the close button, usually a header or title for the overlay.
 * @slot content - Contents that sit inside the overlay.
 */
@Component({
  tag: 'content-overlay',
  styleUrl: 'content-overlay.scss',
  shadow: false,
})
export class ContentOverlay {
  @Element() hostElement: HTMLStencilElement;
  /**
   * @type String
   * @responsive
   */
  @Prop() target: string;

  /**
   * @type Boolean
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  open: ResponsiveBoolean = 'false';
  /**
   * @type Boolean
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  fullScreen: ResponsiveBoolean = 'true';
  /**
   * @type String
   * @responsive
   */
  @Prop() classes: ResponsiveString;
  /**
   * A pipe delimited setting for horizontal alignment. This only applies when fullscreen mode is activated.
   * @type Select
   * @options default
   * @options left
   * @options center
   * @options right
   * @responsive
   */
  @Prop({
    reflect: true,
    mutable: true,
  })
  horizontal: ContentLineTextAlignOptions = 'center';
  /**
   * A pipe delimited setting for vertical alignment. This only applies when fullscreen mode is activated.
   * @type Select
   * @options top
   * @options center
   * @options bottom
   */
  @Prop({
    reflect: true,
    mutable: true,
  })
  vertical: ContentVerticalAlignments = 'center';

  /**
   * Title attribute value for the close button.
   * @type String
   */
  @Prop() closeTitle: string = 'Close Overlay';
  /**
   * Position of the overlay.
   * @type Select
   * @options absolute
   * @options fixed
   * @options static
   * @options sticky
   */
  @Prop({
    reflect: true,
    mutable: true,
  })
  position: ResponsiveString = 'fixed';
  @State() isActive: boolean = false;
  @State() activeClass: string = '';
  @State() classString: string = '';
  @Event({
    bubbles: true,
  })
  contentOverlayActivated: EventEmitter<HTMLElement>;

  @State() contentSlot: HTMLElement;

  dialogElement: HTMLDivElement;
  contentElement: HTMLDivElement;

  @State() isFullScreen: boolean = false;
  @State() hasCloseButton: boolean = false;
  @State() originalMarkup: string;
  ariaDialog;
  focusTrap;
  @Listen('toggle')
  onContentBlockToggle(event: CustomEvent) {
    if (this.hostElement.getAttribute('id') === event.detail.getAttribute('id')) {
      event.preventDefault();
      if (!this.isActive) {
        this.openOverlay();
      } else {
        this.closeOverlay();
      }
    }
  }
  @Listen('open')
  onContentBlockActivated(event: CustomEvent) {
    if (this.hostElement.getAttribute('id') === event.detail.getAttribute('id')) {
      event.preventDefault();
      this.openOverlay();
    }
  }
  @Listen('close')
  onContentBlockDeactivated(event: CustomEvent) {
    if (this.hostElement.getAttribute('id') === event.detail.getAttribute('id')) {
      event.preventDefault();
      this.closeOverlay();
    }
  }

  @Method()
  async openOverlay() {
    this.isActive = true;
    document.body.classList.add(`content-overlay-open`, `content-overlay-open--${this.position}`);
    document.body.classList.remove('content-overlay-close');
    if (!this.originalMarkup) {
      this.originalMarkup = this.contentSlot.innerHTML;
    }
    if (this.target) {
      let targetElement = this.target === '#' ? this.hostElement.parentElement : document.querySelector(this.target);
      if (targetElement) {
        targetElement.classList.add('content-block-overlay-container');
        targetElement.appendChild(this.hostElement);
      }
      // } else if (this.isFullScreen) {
      // if (document.querySelectorAll(`#${this.hostElement.getAttribute('id')}`).length === 1) {
      //   document.body.appendChild(this.hostElement);
      // }
    }

    if (this.originalMarkup) {
      this.contentSlot.innerHTML = this.originalMarkup;
      if (this.contentSlot.children[0] === this.contentSlot.querySelector('script[slot="content"][type="text/template"]')) {
        this.contentSlot.innerHTML = this.contentSlot.children[0].innerHTML;
      }
      this.setComputedActiveClass();
      this.focusTrap = focusTrap.createFocusTrap(this.hostElement, {
        fallbackFocus: this.hostElement,
        allowOutsideClick: true,
        clickOutsideDeactivates: true,
        onDeactivate: () => {
          this.isActive = false;
          document.body.classList.add('content-overlay-close');
          document.body.classList.remove(`content-overlay-open`, `content-overlay-open--${this.position}`);
          this.activeClass = '';
          this.setComputedActiveClass();
          this.contentSlot.innerHTML = '';
        },
      });
    }
    this.contentOverlayActivated.emit(this.hostElement);
  }

  @Method()
  async closeOverlay() {
    if (this.focusTrap) {
      try {
        this.focusTrap.deactivate();
      } catch (e) {
        console.log(e);
      }
    }
  }

  setComputedActiveClass() {
    this.activeClass = `content-overlay ${this.isActive ? 'content-overlay-active' : 'content-overlay-inactive'}  content-overlay--${this.position}`;
  }

  componentWillLoad() {
    WindowResizeService.registerProp(this.classes).subscribe(newValue => (this.classString = `${newValue}`));
    WindowResizeService.registerProp(this.position).subscribe(newValue => (this.position = newValue as string));
    WindowResizeService.registerProp(this.horizontal).subscribe(newValue => this.hostElement.setAttribute('horizontal', newValue as ContentLineTextAlignOptions));
    WindowResizeService.registerProp(this.vertical).subscribe(newValue => this.hostElement.setAttribute('vertical', newValue as ContentVerticalAlignments));
    WindowResizeService.registerProp(this.fullScreen).subscribe(newValue => {
      this.fullScreen = newValue as string;
      this.isFullScreen = (newValue as string) === 'true';
    });
    WindowResizeService.registerProp(this.open).subscribe(newValue => {
      this.hostElement.setAttribute('open', newValue as string);
    });
    this.hasCloseButton = !!this.hostElement.querySelector('[slot="closeButton"]');
  }

  closeOverlayButtonClicked(event) {
    event.preventDefault();
    this.closeOverlay();
  }

  render() {
    return (
      <Host class={`${this.activeClass} ${this.classString}`} position={this.position}>
        <div class="content-block-overlay-container">
          <div class={`content-overlay-background ${this.isActive ? 'active' : ''}`}>
            <div ref={element => (this.dialogElement = element)} role="dialog" aria-modal="true" class={this.isActive ? 'active' : 'hidden'} aria-expanded={this.isActive}>
              <div class="content-overlay__header">
                <button
                  onClick={e => {
                    this.closeOverlayButtonClicked(e);
                  }}
                  class="content-overlay__close"
                  title={this.closeTitle}
                >
                  {!this.hasCloseButton && (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                    </svg>
                  )}
                  <slot name="closeButton"></slot>
                </button>
                <slot name="header"></slot>
              </div>
              <div class="content-overlay__content" ref={(el: HTMLDivElement) => (this.contentSlot = el)}>
                <slot name="content"></slot>
                <slot></slot>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }

  componentDidRender() {
    if (this.open === 'true') {
      this.openOverlay();
    }
    if (this.isActive && this.focusTrap && !this.focusTrap.active) {
      try {
        this.focusTrap.activate();
      } catch (e) {
        console.log(e);
      }
    }
  }
}
